import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { DocumentObjectService } from '@Core/models/proprietary/document/document-object.service';
import { AlertDialogService } from '@Core/services/proprietary/alert-dialog/alert-dialog.service';
import { DataSourceStatus } from '@Core/services/proprietary/datasource';
import { DocumentFileService, DocumentFilingType, DocumentType } from '@Core/services/proprietary/document-file.service';
import { SupportedExtentions } from './document-import.types';

@Component({
    standalone: false,
    selector: 'ep-document-import',
    templateUrl: './document-import.component.html'
})
export class DocumentImportComponent {
    @Input()
    autoFileData: DocumentFilingType;

    @Output()
    docUploadStart = new EventEmitter();

    @Output()
    docComplete = new EventEmitter();

    @Output()
    docFailed = new EventEmitter();

    @Output()
    allDocsComplete = new EventEmitter();

    public model = {
        inProgress: false,
        importComplete: false,
        totalImportCount: 0,
        importNumber: 0,
        failedCount: 0,
        importedDocuments: []
    };

    importedDocumentSub: Subscription;

    constructor(
        public documentOS: DocumentObjectService,
        private documentFile: DocumentFileService,
        private alertService: AlertDialogService
    ) { }

    filesSelected(event: any) {
        let fileList: Array<File> = [];
        for (let i = 0; i < event.target.files.length; i++) {
            fileList.push(event.target.files[i]);
        }
        this.handleFiles(fileList);
    }

    onFilesDropped(fileList: Array<File>) {
        this.handleFiles(fileList);
    }

    private async handleFiles(fileList: Array<File>) {
        let invalidFiles = this.splitOutInvalidFiles(fileList);

        if (invalidFiles.length > 0) {
            let message = 'The following document(s) could not be imported due to an unsupported file type:\n\n';
            for (let i = 0; i < invalidFiles.length; i++) {
                message += invalidFiles[i].name + '\n';
            }
            message += '\nSupported file types are ';

            for (var i in SupportedExtentions) {
                if (i == (SupportedExtentions.length - 1).toString()) {
                    message += 'and ';
                }
                message += SupportedExtentions[i].toUpperCase();
                if (i != (SupportedExtentions.length - 1).toString()) {
                    message += ', ';
                }
            }

            this.alertService.showAlert('Unsupported File Type', message, 'OK');
        }
        if (fileList.length > 0) {
            this.model.importComplete = false;
            this.model.inProgress = true;
            this.model.importNumber = 0;
            this.model.failedCount = 0;
            this.model.importedDocuments = [];

            this.model.totalImportCount = fileList.length;

            for (let i = 0; i < fileList.length; i++) {
                // Emit Upload Started
                this.docUploadStart.emit();

                if (await this.importDocument(fileList[i])) {
                    this.nextDocument(false);
                } else {
                    this.nextDocument(true);
                }
            }
        }
    }

    private splitOutInvalidFiles(fileList: Array<File>): Array<File> {
        let invalidFiles: Array<File> = [];
        for (let i = fileList.length - 1; i >= 0; i--) {
            const fileExtension = fileList[i].name.split('.').pop().toLowerCase();
            if (!SupportedExtentions.includes(fileExtension)) {
                invalidFiles.push(fileList[i]);
                fileList.splice(i, 1);
            }
        }

        return invalidFiles;
    }

    private async importDocument(file: File) {
        return new Promise(async (resolve) => {
            const fileName = file.name;
            const fileExtension = file.name.split('.').pop().toLowerCase();
            let docFormat = '';

            if (SupportedExtentions.includes(fileExtension)) {
                switch (fileExtension) {
                    case 'wav':
                        docFormat = 'AUD';
                        break;

                    case 'mp4':
                        docFormat = 'VID';
                        break;

                    case 'bmp':
                        docFormat = 'IMG';
                        break;

                    case 'txt':
                        docFormat = 'MSW';
                        break;

                    default:
                        docFormat = fileExtension;
                        break;
                }

                let fileContents = await this.getFileAsBase64Async(file);

                const document: DocumentType = {
                    Doc_ID: '0',
                    Doc_Name: fileName,
                    Doc_Desc: fileName,
                    Doc_Class: '?',
                    Doc_Data: { Doc_Format: docFormat, Cdata: fileContents }
                };

                if (this.autoFileData && (this.autoFileData.Entity || this.autoFileData.Contract || this.autoFileData.Claim)) {
                    this.documentFile.addNewDocumentWithFiling(document, [this.autoFileData]);
                } else {
                    this.documentFile.addNewDocumentWithoutFiling(document);
                }

                this.importedDocumentSub = this.documentFile.dsDocument.status$.subscribe((x) => {
                    switch (x) {
                        case DataSourceStatus.Ready:
                            if (this.importedDocumentSub) {
                                this.importedDocumentSub.unsubscribe();
                            }
                            this.model.importedDocuments.push(this.documentFile.dsDocument.objectNode);
                            resolve(true);
                            break;

                        case DataSourceStatus.Error:
                            if (this.importedDocumentSub) {
                                this.importedDocumentSub.unsubscribe();
                            }
                            resolve(false);
                            break;
                    }
                });
            } else {
                resolve(false);
            }
        });
    }

    private nextDocument(docFailed: boolean = false) {
        if (docFailed) {
            this.docFailed.emit();
            this.model.failedCount++;
        } else {
            this.docComplete.emit();
        }
        this.model.importNumber++;
        if (this.model.importNumber == this.model.totalImportCount) {
            this.allDocumentsCompleted();
        }
    }

    private async allDocumentsCompleted() {
        if (this.importedDocumentSub) {
            this.importedDocumentSub.unsubscribe();
        }
        this.model.importComplete = true;
        this.model.inProgress = false;
        this.allDocsComplete.emit(this.model.importedDocuments);
        await this.setTimer(2);
        this.model.importComplete = false;
        this.model.importedDocuments = [];
    }

    private async getFileAsBase64Async(file: File) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',').pop());
            reader.readAsDataURL(file);
        });
    }

    setTimer(seconds: number) {
        return new Promise(async (resolve) => {
            let intervalId = setInterval(() => {
                seconds--;
                if (seconds === 0) {
                    clearInterval(intervalId);
                    resolve(true);
                }
            }, 1000);
        });
    }
}
